import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { Loading, Button } from '../../../components';
import { DisplayUtils, constants } from '../../../utils';
import './SSOSignup.scss';
import { useParametrics } from '../../../contexts/ParametricsContext';
import { useErrorsContext } from '../../../contexts/ErrorsContext';
import { useForm } from 'react-hook-form';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import { updatePetitionSSOSignup, getPetitionSSOSignup, sendSSOAuthCode, getDocumentation } from '../../../services/petitionServices';
import ReCAPTCHA from "react-google-recaptcha";

const SSOSignup = () => {

  const { t } = useTranslation();
  const { convocationId } = useParametrics();
  const { showError } = useErrorsContext();
  const location = useLocation();
  const history = useHistory();
  const userType = location?.state?.userType;
  const petitionId = location?.state?.petitionId;
  const petitionHasAccount = location?.state?.petitionHasAccount;
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mailSent, setMailSent] = useState(false);
  const [timer, setTimer] = useState(30);
  const localData = JSON.parse(localStorage.getItem('dataSignup'));
  const correosSignup = location?.state?.correos;
  const { currentLanguage, changeLanguage } = useLanguageContext();
  const [userDocs, setUserDocs] = useState([]);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: localData?.email || ''
    },
  });

  const code = watch('code');

  const nextStep = async () => {
    try {
      history.replace(`/${userType}/join/sign-request`, { signRequest: true, userType: userType, petitionId: petitionId, petitionHasAccount: true, ssoEmail: true })
    } catch (e) {
      showError(e);
    } finally {
      setLoading(true);
    }
  }

  const getDocuments = async () => {
    try {
      const resp = await getDocumentation(convocationId, userType.toUpperCase(), petitionId);
      setUserDocs(resp.documentList)
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  }

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPetitionSSOSignup(convocationId, userType.toUpperCase(), petitionId);
      if (data.completedStep) {
        nextStep();
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [showError, convocationId]); // eslint-disable-line

  const onChangeRecaptcha = () => {
    setCaptchaOk(true);
  }


  useEffect(() => {
    convocationId && getData();
    getDocuments();
  }, [convocationId]) //eslint-disable-line


  const password = useRef();
  const confirmPassword = useRef();
  const email = useRef();
  password.current = watch('password', '');
  confirmPassword.current = watch('confirmPassword', '');
  email.current = watch('email', '');

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      // Update Status
      await updatePetitionSSOSignup(
        convocationId, userType.toUpperCase(), petitionId, {
        email: data.email,
        authCode: data.code,
        password: data.password,
      }
      );

      nextStep();


    } catch (e) {
      setCaptchaOk(false);
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCutPaste = (event) => {
    event.preventDefault();
  };

  const sendCode = async () => {
    try {
      setMailSent(true);
      await sendSSOAuthCode(convocationId, userType.toUpperCase(), petitionId, { email: email.current });
      setTimer(30);
      // Clear any existing interval before starting a new one
      if (window.timerInterval) {
        clearInterval(window.timerInterval);
      }
      window.timerInterval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(window.timerInterval);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    } catch (e) {
      showError(e);
      setMailSent(false);
      setTimer(30);
      // Clear any existing interval before starting a new one
      if (window.timerInterval) {
        clearInterval(window.timerInterval);
      }
    }
  }

  const resetEmail = async () => {
    if (timer > 0) {
      return;
    } else {
      setMailSent(false);
      setTimer(30);
      // Clear any existing interval before starting a new one
      if (window.timerInterval) {
        clearInterval(window.timerInterval);
      }
      window.timerInterval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(window.timerInterval);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
  }


  useEffect(() => {

    if (!convocationId) return;
    if (!location?.state?.ssoSignup || !userType || !petitionId || petitionHasAccount === undefined) {
      history.goBack()
    } else {
      setShow(true);
    }
  }, [convocationId]) //eslint-disable-line

  return (
    show && (
      loading ? (
        <div className='vh-100 d-flex justify-content-center align-items-center'>
          <Loading />
        </div>
      ) : (
        <div className='SSOSignup'>
          <div className="w-100 d-flex justify-content-center">
            {!DisplayUtils.isMobile() ? (
              <img src="/misc/icons/complete_logo_white.svg" alt="shop-icon" className="RequestInfo__logo" />
            ) : (
              <div className="SSOSignup__title">
                <Button
                  icon="/misc/icons/arrow-left-blue.svg"
                  action={() => {
                    if (userDocs && userDocs.length > 0) {
                      history.push({ pathname: `/${userType}/join/upload-documentation`, state: { uploadDocumentation: true, userType: userType, petitionId: petitionId, petitionHasAccount } })
                    } else {
                      history.push({ pathname: `/${userType}/join/notifications-address`, state: { notificationsAddressInfo: true, userType: userType, petitionId: petitionId, petitionHasAccount } })
                    }
                  }}
                  className="p-2 button-only-icon me-2"
                  a11y={t('Globals.return')}
                />
                <h1>{t('PetitionSignup.title')}</h1>
              </div>
            )}
          </div>
          <div className="w-100 d-flex justify-content-center">
            <div className="SSOSignup__container">
              {!DisplayUtils.isMobile() && (
                <div className="SSOSignup__container__head">
                  <Button
                    icon="/misc/icons/arrow-left-pink.svg"
                    action={() => {
                      if (userDocs && userDocs.length > 0) {
                        history.push({ pathname: `/${userType}/join/upload-documentation`, state: { uploadDocumentation: true, userType: userType, petitionId: petitionId, petitionHasAccount } })
                      } else {
                        history.push({ pathname: `/${userType}/join/notifications-address`, state: { notificationsAddressInfo: true, userType: userType, petitionId: petitionId, petitionHasAccount } })
                      }
                    }}
                    className="p-2 button-only-icon me-2"
                    a11y={t('Globals.return')}
                  />
                  <h1>{t('PetitionSignup.title')}</h1>
                </div>
              )}
              <div className="Signup__container__form">
                {correosSignup && (
                  <div className='Signup__containerLang'>
                    <div className="Signup__containerLang__lang">
                      <button type='button' className={`Signup__containerLang__lang__tooltip ${currentLanguage !== "es" ? "disabled" : ""}`} onClick={() => changeLanguage("es")}>
                        <img src='/misc/icons/es.png' alt='lang' />
                        <span className="tooltiptext">{t('Language.spanish')}</span>
                      </button>
                      <button type='button' className={`Signup__containerLang__lang__tooltip ${currentLanguage !== "ca" ? "disabled" : ""}`} onClick={() => changeLanguage("ca")}>
                        <img src='/misc/icons/ca.png' alt='lang' />
                        <span className="tooltiptext">{t('Language.catalan')}</span>
                      </button>
                      <button type='button' className={`Signup__containerLang__lang__tooltip ${currentLanguage !== "ga" ? "disabled" : ""}`} onClick={() => changeLanguage("ga")}>
                        <img src='/misc/icons/gallego.svg' alt='lang' />
                        <span className="tooltiptext">{t('Language.gallego')}</span>
                      </button>
                      <button type='button' className={`Signup__containerLang__lang__tooltip ${currentLanguage !== "eu" ? "disabled" : ""}`} onClick={() => changeLanguage("eu")}>
                        <img src='/misc/icons/euskera.svg' alt='lang' />
                        <span className="tooltiptext">{t('Language.euskera')}</span>
                      </button>
                      <button type='button' className={`Signup__containerLang__lang__tooltip ${currentLanguage !== "va" ? "disabled" : ""}`} onClick={() => changeLanguage("va")}>
                        <img src='/misc/icons/valenciano.svg' alt='lang' />
                        <span className="tooltiptext">{t('Language.valenciano')}</span>
                      </button>
                    </div>
                  </div>
                )}
                <p className="Signup__container__description">{t('PetitionSignup.description')}</p>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <label> {t('PetitionSignup.email.label')}</label>
                  <div className='d-flex justify-content-space-between'>
                    <div className="Signup__container__form__box d-flex">
                      <div className="flex-grow-1 position-relative">
                        <input
                          disabled={mailSent}
                          type="text"
                          placeholder={t('PetitionSignup.email.placeholder')}
                          className={`${errors.email ? 'warning' : ''}`}
                          {...register('email', {
                            required: t('Globals.required'),
                            validate: (value) =>
                              constants.EMAIL_PATTERN.test(value) || t('Errors.notValid'),
                          })}
                          onCopy={handleCopyCutPaste}
                          onCut={handleCopyCutPaste}
                          onPaste={handleCopyCutPaste}
                        />
                      </div>
                    </div>
                    <Button
                      disabled={mailSent || errors.email || email.current === null || email.current === ""}
                      className="buttonPrimaryShort mx-1"
                      text={t('PetitionSignup.email.send')}
                      action={() => sendCode()}
                      styles={{ backgroundColor: '#34fdfe', color: '#381940', width: '10em' }}
                    />
                  </div>
                  {errors.email && (
                    <p className="Signup__container__form__error">{errors.email.message}</p>
                  )}

                  {mailSent && <span className='SSOSignup__container__reset_email' onClick={() => resetEmail()}> {t('PetitionSignup.email.reset')} {timer > 0 ? timer + 's' : t('PetitionSignup.email.resetTimer')}</span>}

                  <>
                    <div className="Signup__container__content mt-4">
                      <div className="Signup__container__content__applicant-observations">
                        <label> {t('Petitions.ChangeEmail.verification.code')}<b style={{ "color": "#f838f9" }}>*</b></label>
                        <div className='input-container'>
                          <input
                            disabled={!mailSent}
                            autoComplete='off'
                            name='code'
                            type='text'
                            {...register('code', {
                              required: t('Globals.required'),
                            })}
                          />
                        </div>
                        {errors.code && (
                          <p className="Signup__container__form__error">{errors.code.message}</p>
                        )}
                      </div>
                      <div>
                        <label>{t('PetitionSignup.password.label')}</label>
                        <div className="Signup__container__form__box">
                          <input
                            disabled={!mailSent}
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('PetitionSignup.password.placeholder')}
                            className={`${errors.password ? 'warning' : ''}`}
                            {...register('password', {
                              required: t('Globals.required'),
                              onChange: () => {
                                setTimeout(() => {
                                  if (confirmPassword.current !== '') trigger('confirmPassword');
                                }, 100)
                              },
                              validate: (value) => {
                                return constants.PASSWORD_PATTERN.test(value) || t('PetitionSignup.password.invalidFormat');
                              }
                            })}
                            onCopy={handleCopyCutPaste}
                            onCut={handleCopyCutPaste}
                            onPaste={handleCopyCutPaste}
                          />
                          <img src={showPassword ? '/misc/icons/eye-show.png' : '/misc/icons/eye-hide.png'} alt="icon" style={{ width: showPassword ? '26px' : '24px', cursor: 'pointer' }} onClick={() => setShowPassword(!showPassword)} />
                        </div>
                        {errors.password && (
                          <p className="Signup__container__form__error">{errors.password.message}</p>
                        )}
                      </div>
                      <div>
                        <label>{t('PetitionSignup.confirm.label')}</label>
                        <div className="Signup__container__form__box">
                          <input
                            disabled={!mailSent}
                            type={showRepeatPassword ? 'text' : 'password'}
                            placeholder={t('PetitionSignup.confirm.placeholder')}
                            className={`${errors.confirmPassword ? 'warning' : ''}`}
                            {...register('confirmPassword', {
                              required: t('Globals.required'),
                              validate: (value) => {
                                return value === password.current || t('Errors.confNotEqual');
                              }
                            })}
                            onCopy={handleCopyCutPaste}
                            onCut={handleCopyCutPaste}
                            onPaste={handleCopyCutPaste}
                          />
                          <img src={showRepeatPassword ? '/misc/icons/eye-show.png' : '/misc/icons/eye-hide.png'} alt="icon" style={{ width: showRepeatPassword ? '26px' : '24px', cursor: 'pointer' }} onClick={() => setShowRepeatPassword(!showRepeatPassword)} />
                        </div>
                        {errors.confirmPassword && (
                          <p className="Signup__container__form__error">
                            {errors.confirmPassword.message}
                          </p>
                        )}
                        <p
                          className="Signup__container__description"
                          style={{ marginBottom: '32px', marginTop: '20px' }}
                        >
                          {t('PetitionSignup.required')}
                        </p>
                      </div>
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: t('PetitionSignup.info') }} className="Signup__container__form__info" style={{ marginTop: correosSignup ? '20px' : '0px' }} />
                    <div className="d-flex justify-content-center" style={{ marginTop: "24px", marginBottom: DisplayUtils.isMobile() ? '24px' : '24px' }}>
                      <ReCAPTCHA
                        sitekey="6LclvPAgAAAAAK7ePe7Kb8sSKkMOjAlkUnrgzOXO"
                        onChange={onChangeRecaptcha}
                      />
                    </div>
                  </>


                  <div className="SSOSignup__container__actions">
                    <Button
                      className="buttonPrimaryLarge"
                      text={t('Join.uploadDocumentation.later')}
                      styles={{ backgroundColor: '#f4f5fb', color: '#312c6a' }}
                      action={() => history.push({ pathname: `/join/leave`, state: { leave: true } })}
                    />
                    <Button
                      className="buttonPrimaryLarge"
                      text={t('Join.SSOSignup.action')}
                      disabled={!captchaOk || Object.keys(errors).length !== 0 || !mailSent || !code}
                      type="submit"
                      styles={{
                        backgroundColor: '#34fdfe',
                        color: '#381940',
                        marginBottom: '23px',
                      }}
                    />
                  </div>

                </form>
              </div>
            </div>
          </div>
        </div>)
    )
  )
}

export default SSOSignup;